import { tC } from "../../utils/TranslateWithContentLanguage";
import Markdown from "react-markdown";
import CardImage from "../tool_components/CardImage";
import AiHomeworkIdeaCardPdf from "./AiHomeworkIdeaCardPdf";
import PdfDownloadButton from "../pdf/PdfDownloadButton";
import useImagePdf from "../pdf/useImagePdf";
import { Button } from "@core/index";
import { RefreshCw } from "lucide-react";
import { MascotBubble } from "../tool_components/MascotBubble";
import { useState } from "react";
import { useAppSelector } from "@packages/store/hooks";
import PdfCustomizationModal, {
  PdfCustomizationSettings,
} from "@features/common/components/pdf-customization-modal";
import {
  getGetAiResistantHomeworkQueryKey,
  getGetUserAiResistantHomeworkQueryKey,
  updateAiResistantHomework,
} from "@packages/api/madlenAPI";
import useIsAuthor from "@packages/hooks/useIsAuthor";
import RegeneratingOverlay from "../tool_components/RegenLoadingOverlay";
import { BaseTeacherToolResponseDtoStatus as HomeworkIdeaResponseDtoStatus } from "@packages/api/madlenAPI.schemas";
import EditableMarkdown from "../tool_components/EditableMarkdown";
import { useQueryClient } from "@tanstack/react-query";

// Helper to get complexity level colors
export const getComplexityColor = (level: string) => {
  if (level === "BEGINNER") {
    return {
      bg: "bg-green-100 dark:bg-green-900",
      text: "text-green-800 dark:text-green-200",
      border: "border-green-500",
    };
  } else if (level === "INTERMEDIATE") {
    return {
      bg: "bg-amber-100 dark:bg-amber-900",
      text: "text-amber-800 dark:text-amber-200",
      border: "border-amber-500",
    };
  } else if (level === "ADVANCED") {
    return {
      bg: "bg-red-100 dark:bg-red-900",
      text: "text-red-800 dark:text-red-200",
      border: "border-red-500",
    };
  }

  // Default teal colors
  return {
    bg: "bg-teal-100 dark:bg-teal-900",
    text: "text-teal-800 dark:text-teal-200",
    border: "border-teal-500",
  };
};

export interface AiHomeworkIdeaCardProps {
  homeworkIdea: {
    id: string;
    contentLanguage: string;
    imageUrl?: string;
    videoUrl?: string;
    title?: string;
    topic: string;
    subject: string;
    gradeLevel: number;
    duration: string;
    difficulty: string;
    description: string;
    reportIdeas?: Array<{
      title: string;
      description: string;
      explanation: string;
      complexity_level: string;
      estimated_time?: string;
      purpose_alignment?: string;
      resources?: {
        digital_resources?: string[];
        printed_materials?: string[];
        tools_and_materials?: string[];
      };
      teacher_guidelines?: {
        feedback_tips?: string[];
        grading_criteria?: string[];
        time_estimation?: string;
      };
      student_criteria?: {
        self_assessment_questions?: string[];
        success_indicators?: string[];
      };
      recommended_resources?: string[];
      outline?: string;
    }>;
    // Legacy field for backward compatibility
    ideas?: Array<{
      title: string;
      description: string;
      explanation: string;
      complexity_level: string;
      estimated_time?: string;
      purpose_alignment?: string;
      resources?: {
        digital_resources?: string[];
        printed_materials?: string[];
        tools_and_materials?: string[];
      };
      teacher_guidelines?: {
        feedback_tips?: string[];
        grading_criteria?: string[];
        time_estimation?: string;
      };
      student_criteria?: {
        self_assessment_questions?: string[];
        success_indicators?: string[];
      };
      recommended_resources?: string[];
      outline?: string;
    }>;
    authorName: string;
    authorId: string;
    likeCount: number;
    didUserLiked: boolean;
    status?: HomeworkIdeaResponseDtoStatus;
    createdAt: string;
    isEditable?: boolean;
  };
  isImageLoading?: boolean;
  customizationSettings?: PdfCustomizationSettings;
  isEditable?: boolean;
  onCreateAgain?: () => void;
}

export const AiHomeworkIdeaCard = ({
  homeworkIdea,
  isImageLoading,
  onCreateAgain,
  isEditable = false,
}: Omit<AiHomeworkIdeaCardProps, "customizationSettings">) => {
  const finalImageUrl = useImagePdf(homeworkIdea.imageUrl);
  const contentLanguage = homeworkIdea.contentLanguage || "tr";
  const [showPdfCustomizationModal, setShowPdfCustomizationModal] =
    useState(false);
  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  );
  const isAuthor = useIsAuthor(parseInt(homeworkIdea.authorId));
  const queryClient = useQueryClient();

  // Use the isEditable prop from props or from the homeworkIdea object
  const isContentEditable = isEditable || homeworkIdea.isEditable || false;

  // Handle backward compatibility - use reportIdeas if available, otherwise fall back to ideas
  const ideas = homeworkIdea.reportIdeas || homeworkIdea.ideas || [];

  return (
    <div className="space-y-8">
      {isAuthor && (
        <MascotBubble
          toolsQueryKey={getGetUserAiResistantHomeworkQueryKey()}
          toolQueryKey={getGetAiResistantHomeworkQueryKey(
            parseInt(homeworkIdea.id),
          )}
          createChatParams={{
            teacherToolId: parseInt(homeworkIdea.id),
            toolType: "AI_RESISTANT_HOMEWORK",
          }}
        />
      )}
      {/* Header Section */}
      <CardImage
        imageUrl={homeworkIdea.imageUrl}
        videoUrl={homeworkIdea.videoUrl}
        courseName={homeworkIdea.subject}
        gradeLevel={homeworkIdea.gradeLevel}
        theme="teal"
        isLoading={isImageLoading}
        createdAt={homeworkIdea.createdAt}
        topic={homeworkIdea.title || homeworkIdea.topic}
        badges={null}
        contentLanguage={contentLanguage}
      />
      <div className="flex justify-end items-center mt-4 gap-2">
        {onCreateAgain && (
          <Button
            onClick={onCreateAgain}
            variant="solid"
            size="md"
            textColor="text-white dark:text-white"
            bgColor="bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 dark:hover:from-teal-700 dark:hover:to-teal-800"
            rounded="xl"
          >
            <RefreshCw size={18} />
            <span>{tC(contentLanguage, "common.createAnother")}</span>
          </Button>
        )}
        <PdfDownloadButton
          pdfTitle={homeworkIdea.title ?? ""}
          openCustomizationModal={() => setShowPdfCustomizationModal(true)}
          document={
            <AiHomeworkIdeaCardPdf
              customizationSettings={
                customizationSettings as PdfCustomizationSettings | undefined
              }
              homeworkIdea={{ ...homeworkIdea, imageUrl: finalImageUrl }}
              onCreateAgain={onCreateAgain}
            />
          }
          btnTheme="bg-gradient-to-r from-teal-500 to-teal-600"
        />
      </div>
      {/* Ideas Section */}
      <div className="space-y-1 relative">
        {ideas.map((idea, index) => (
          <div
            key={index}
            className="bg-white dark:bg-gray-800 rounded-xl p-7 shadow-sm border border-gray-200 dark:border-gray-700"
          >
            <div className="space-y-6">
              <div className="flex justify-between items-start mb-2">
                <div className="prose dark:prose-invert font-semibold">
                  <EditableMarkdown
                    value={idea.title}
                    isEditable={isContentEditable}
                    handleSave={async (newValue) => {
                      if (!ideas.length) return;

                      const updatedIdeas = [...ideas];
                      updatedIdeas[index] = {
                        ...updatedIdeas[index],
                        title: newValue,
                      };

                      await updateAiResistantHomework(
                        parseInt(homeworkIdea.id),
                        {
                          reportIdeas: updatedIdeas,
                        },
                      );

                      await queryClient.invalidateQueries({
                        queryKey: getGetUserAiResistantHomeworkQueryKey(),
                      });
                      await queryClient.invalidateQueries({
                        queryKey: getGetAiResistantHomeworkQueryKey(
                          parseInt(homeworkIdea.id),
                        ),
                      });
                    }}
                  />
                </div>
                {/* Use dynamic complexity level colors and estimated time */}
                <div className="flex items-center gap-2 ml-3">
                  {idea.complexity_level && (
                    <span
                      className={`px-3 py-1 text-xs font-medium rounded-full ${getComplexityColor(idea.complexity_level).bg} ${getComplexityColor(idea.complexity_level).text}`}
                    >
                      {tC(
                        contentLanguage,
                        `aiHomeworkIdeas.complexity.${idea.complexity_level}`,
                      )}
                    </span>
                  )}
                  {idea.estimated_time && (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200">
                      {idea.estimated_time}
                    </span>
                  )}
                </div>
              </div>

              <div className="mt-6">
                <h4 className="text-lg font-semibold text-teal-700 dark:text-teal-300 mb-4">
                  {tC(contentLanguage, "aiHomeworkIdeas.assignmentDescription")}
                </h4>
                <div
                  className={`pl-4 border-l-4 ${getComplexityColor(idea.complexity_level).border} pt-1 pb-1`}
                >
                  <div className="prose dark:prose-invert max-w-none whitespace-pre-line space-y-4">
                    <EditableMarkdown
                      value={idea.description}
                      isEditable={isContentEditable}
                      handleSave={async (newValue) => {
                        if (!ideas.length) return;

                        const updatedIdeas = [...ideas];
                        updatedIdeas[index] = {
                          ...updatedIdeas[index],
                          description: newValue,
                        };

                        await updateAiResistantHomework(
                          parseInt(homeworkIdea.id),
                          {
                            reportIdeas: updatedIdeas,
                          },
                        );

                        await queryClient.invalidateQueries({
                          queryKey: getGetUserAiResistantHomeworkQueryKey(),
                        });
                        await queryClient.invalidateQueries({
                          queryKey: getGetAiResistantHomeworkQueryKey(
                            parseInt(homeworkIdea.id),
                          ),
                        });
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-7">
                <h4 className="text-lg font-semibold text-teal-700 dark:text-teal-300 mb-4">
                  {tC(contentLanguage, "aiHomeworkIdeas.whyAiResistant")}
                </h4>
                <div
                  className={`pl-4 border-l-4 ${getComplexityColor(idea.complexity_level).border} pt-1 pb-1`}
                >
                  <div className="prose dark:prose-invert max-w-none">
                    <EditableMarkdown
                      value={idea.explanation}
                      isEditable={isContentEditable}
                      handleSave={async (newValue) => {
                        if (!ideas.length) return;

                        const updatedIdeas = [...ideas];
                        updatedIdeas[index] = {
                          ...updatedIdeas[index],
                          explanation: newValue,
                        };

                        await updateAiResistantHomework(
                          parseInt(homeworkIdea.id),
                          {
                            reportIdeas: updatedIdeas,
                          },
                        );

                        await queryClient.invalidateQueries({
                          queryKey: getGetUserAiResistantHomeworkQueryKey(),
                        });
                        await queryClient.invalidateQueries({
                          queryKey: getGetAiResistantHomeworkQueryKey(
                            parseInt(homeworkIdea.id),
                          ),
                        });
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Purpose Alignment */}
              {idea.purpose_alignment && (
                <div className="mt-7">
                  <h5 className="text-md font-semibold text-gray-800 dark:text-white mb-4">
                    {tC(contentLanguage, "aiHomeworkIdeas.purposeAlignment")}
                  </h5>
                  <div className="pl-4 border-l-4 border-blue-300 dark:border-blue-600 pt-1 pb-1">
                    <div className="prose dark:prose-invert">
                      <EditableMarkdown
                        value={idea.purpose_alignment}
                        isEditable={isContentEditable}
                        handleSave={async (newValue) => {
                          if (!ideas.length) return;

                          const updatedIdeas = [...ideas];
                          updatedIdeas[index] = {
                            ...updatedIdeas[index],
                            purpose_alignment: newValue,
                          };

                          await updateAiResistantHomework(
                            parseInt(homeworkIdea.id),
                            {
                              reportIdeas: updatedIdeas,
                            },
                          );

                          await queryClient.invalidateQueries({
                            queryKey: getGetUserAiResistantHomeworkQueryKey(),
                          });
                          await queryClient.invalidateQueries({
                            queryKey: getGetAiResistantHomeworkQueryKey(
                              parseInt(homeworkIdea.id),
                            ),
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {idea.outline && (
                <div className="mt-7">
                  <h5 className="text-md font-semibold text-gray-800 dark:text-white mb-4">
                    {tC(contentLanguage, "aiHomeworkIdeas.outline")}
                  </h5>
                  <div className="pl-4 border-l-4 border-gray-300 dark:border-gray-600 pt-1">
                    <div className="prose dark:prose-invert">
                      <EditableMarkdown
                        value={idea.outline}
                        isEditable={isContentEditable}
                        handleSave={async (newValue) => {
                          if (!ideas.length) return;

                          const updatedIdeas = [...ideas];
                          updatedIdeas[index] = {
                            ...updatedIdeas[index],
                            outline: newValue,
                          };

                          await updateAiResistantHomework(
                            parseInt(homeworkIdea.id),
                            {
                              reportIdeas: updatedIdeas,
                            },
                          );

                          await queryClient.invalidateQueries({
                            queryKey: getGetUserAiResistantHomeworkQueryKey(),
                          });
                          await queryClient.invalidateQueries({
                            queryKey: getGetAiResistantHomeworkQueryKey(
                              parseInt(homeworkIdea.id),
                            ),
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Resources */}
              {idea.resources && (
                <div className="mt-7">
                  <h5 className="text-md font-semibold text-gray-800 dark:text-white mb-4">
                    {tC(contentLanguage, "aiHomeworkIdeas.resources")}
                  </h5>
                  <div className="pl-4 border-l-4 border-gray-300 dark:border-gray-600 pt-1 pb-1">
                    {idea.resources.digital_resources &&
                      idea.resources.digital_resources.length > 0 && (
                        <div className="mb-6">
                          <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                            {tC(
                              contentLanguage,
                              "aiHomeworkIdeas.digitalResources",
                            )}
                          </h6>
                          <div className="space-y-2">
                            {idea.resources.digital_resources.map(
                              (resource, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-start gap-2"
                                >
                                  <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    •
                                  </span>
                                  <span className="text-sm text-gray-800 dark:text-gray-200">
                                    {resource}
                                  </span>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}

                    {idea.resources.printed_materials &&
                      idea.resources.printed_materials.length > 0 && (
                        <div className="mb-6">
                          <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                            {tC(
                              contentLanguage,
                              "aiHomeworkIdeas.printedMaterials",
                            )}
                          </h6>
                          <div className="space-y-2">
                            {idea.resources.printed_materials.map(
                              (resource, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-start gap-2"
                                >
                                  <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    •
                                  </span>
                                  <span className="text-sm text-gray-800 dark:text-gray-200">
                                    {resource}
                                  </span>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}

                    {idea.resources.tools_and_materials &&
                      idea.resources.tools_and_materials.length > 0 && (
                        <div className="mb-6">
                          <h6 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                            {tC(
                              contentLanguage,
                              "aiHomeworkIdeas.toolsAndMaterials",
                            )}
                          </h6>
                          <div className="space-y-2">
                            {idea.resources.tools_and_materials.map(
                              (resource, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-start gap-2"
                                >
                                  <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    •
                                  </span>
                                  <span className="text-sm text-gray-800 dark:text-gray-200">
                                    {resource}
                                  </span>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              )}

              {/* Legacy recommended resources - keeping for backward compatibility */}
              {idea.recommended_resources && (
                <div className="mt-7">
                  <h5 className="text-md font-semibold text-gray-800 dark:text-white mb-4">
                    {tC(
                      contentLanguage,
                      "aiHomeworkIdeas.recommendedResources",
                    )}
                  </h5>
                  <div className="pl-4 border-l-4 border-gray-300 dark:border-gray-600 pt-1 pb-1">
                    {idea.recommended_resources.map((resource, idx) => (
                      <div
                        key={idx}
                        className="flex flex-row items-start gap-x-3 mb-5"
                      >
                        <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          {idx + 1}.
                        </span>
                        <div className="prose dark:prose-invert whitespace-pre-line space-y-3">
                          <EditableMarkdown
                            value={resource}
                            isEditable={isContentEditable}
                            handleSave={async (newValue) => {
                              if (!ideas.length || !idea.recommended_resources)
                                return;

                              const updatedIdeas = [...ideas];
                              const updatedResources = [
                                ...idea.recommended_resources,
                              ];
                              updatedResources[idx] = newValue;

                              updatedIdeas[index] = {
                                ...updatedIdeas[index],
                                recommended_resources: updatedResources,
                              };

                              await updateAiResistantHomework(
                                parseInt(homeworkIdea.id),
                                {
                                  reportIdeas: updatedIdeas,
                                },
                              );

                              await queryClient.invalidateQueries({
                                queryKey:
                                  getGetUserAiResistantHomeworkQueryKey(),
                              });
                              await queryClient.invalidateQueries({
                                queryKey: getGetAiResistantHomeworkQueryKey(
                                  parseInt(homeworkIdea.id),
                                ),
                              });
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Teacher Guidelines */}
              {idea.teacher_guidelines && (
                <div className="mt-7">
                  <h5 className="text-md font-semibold text-gray-800 dark:text-white mb-4">
                    {tC(contentLanguage, "aiHomeworkIdeas.teacherGuidelines")}
                  </h5>
                  <div className="bg-teal-50 dark:bg-teal-900/20 rounded-lg p-4 border-l-4 border-teal-400">
                    {idea.teacher_guidelines.feedback_tips &&
                      idea.teacher_guidelines.feedback_tips.length > 0 && (
                        <div className="mb-6">
                          <h6 className="text-sm font-semibold text-teal-700 dark:text-teal-300 mb-3">
                            {tC(
                              contentLanguage,
                              "aiHomeworkIdeas.feedbackTips",
                            )}
                          </h6>
                          <div className="space-y-3">
                            {idea.teacher_guidelines.feedback_tips.map(
                              (tip, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-start gap-3"
                                >
                                  <span className="inline-flex items-center justify-center w-6 h-6 bg-teal-100 dark:bg-teal-800 text-teal-600 dark:text-teal-200 rounded-full text-xs font-medium">
                                    {idx + 1}
                                  </span>
                                  <Markdown className="text-sm text-gray-700 dark:text-gray-300">
                                    {tip}
                                  </Markdown>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}

                    {idea.teacher_guidelines.grading_criteria &&
                      idea.teacher_guidelines.grading_criteria.length > 0 && (
                        <div className="mb-6">
                          <h6 className="text-sm font-semibold text-teal-700 dark:text-teal-300 mb-3">
                            {tC(
                              contentLanguage,
                              "aiHomeworkIdeas.gradingCriteria",
                            )}
                          </h6>
                          <div className="space-y-3">
                            {idea.teacher_guidelines.grading_criteria.map(
                              (criteria, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-start gap-3"
                                >
                                  <span className="inline-flex items-center justify-center w-6 h-6 bg-teal-100 dark:bg-teal-800 text-teal-600 dark:text-teal-200 rounded-full text-xs font-medium">
                                    {idx + 1}
                                  </span>
                                  <Markdown className="text-sm text-gray-700 dark:text-gray-300">
                                    {criteria}
                                  </Markdown>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              )}

              {/* Student Criteria */}
              {idea.student_criteria && (
                <div className="mt-7">
                  <h5 className="text-md font-semibold text-gray-800 dark:text-white mb-4">
                    {tC(contentLanguage, "aiHomeworkIdeas.studentCriteria")}
                  </h5>
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border-l-4 border-blue-400">
                    {idea.student_criteria.self_assessment_questions &&
                      idea.student_criteria.self_assessment_questions.length >
                        0 && (
                        <div className="mb-6">
                          <h6 className="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-3">
                            {tC(
                              contentLanguage,
                              "aiHomeworkIdeas.selfAssessmentQuestions",
                            )}
                          </h6>
                          <div className="space-y-3">
                            {idea.student_criteria.self_assessment_questions.map(
                              (question, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-start gap-3"
                                >
                                  <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-200 rounded-full text-xs font-medium">
                                    {idx + 1}
                                  </span>
                                  <Markdown className="text-sm text-gray-700 dark:text-gray-300">
                                    {question}
                                  </Markdown>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}

                    {idea.student_criteria.success_indicators &&
                      idea.student_criteria.success_indicators.length > 0 && (
                        <div className="mb-6">
                          <h6 className="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-3">
                            {tC(
                              contentLanguage,
                              "aiHomeworkIdeas.successIndicators",
                            )}
                          </h6>
                          <div className="space-y-3">
                            {idea.student_criteria.success_indicators.map(
                              (indicator, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-start gap-3"
                                >
                                  <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-200 rounded-full text-xs font-medium">
                                    ✓
                                  </span>
                                  <Markdown className="text-sm text-gray-700 dark:text-gray-300">
                                    {indicator}
                                  </Markdown>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
        {/* Regenerating overlay */}
        {homeworkIdea.status === "REGENERATING" && <RegeneratingOverlay />}
      </div>
      {/* PDF Customization Modal */}
      <PdfCustomizationModal
        show={showPdfCustomizationModal}
        onClose={() => setShowPdfCustomizationModal(false)}
        hideSchoolLogoOptions
        documentComponent={
          <AiHomeworkIdeaCardPdf
            customizationSettings={
              customizationSettings as PdfCustomizationSettings | undefined
            }
            homeworkIdea={{ ...homeworkIdea, imageUrl: finalImageUrl }}
            onCreateAgain={onCreateAgain}
          />
        }
      />
    </div>
  );
};
